import { Button, Card, ErrorMessage } from 'nhsuk-react-components';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import Back from 'components/Back';
import IterationPageHeader from 'components/IterationPageHeader';
import ProgressIndicator from 'components/ProgressIndicator/index.tsx';
import ReviewerPicker from 'components/ReviewerPicker';
import useCampaign from 'hooks/useCampaign';
import useCampaignNavigate from 'hooks/useCampaignNavigate.ts';
import useIterationLock from 'hooks/useIterationLock.ts';
import 'pages/campaign/iteration/IterationReviewersPage.scss';
import useFetchSpecificCampaign from 'queries/fetch/fetchSpecificCampaign.ts';
import useUpdateIterationStatus from 'queries/mutate/updateIterationStatus.ts';
import { ITERATION_STATUS } from 'schemas/CampaignApiDetails.schema.ts';
import { CognitoUser } from 'schemas/UserInfo.schema.ts';
import { IIterationLevelParams } from 'utils/parameterUtils';

function IterationReviewersPage() {
  const { teamId, campaignId, iterationId } = useParams() as IIterationLevelParams;
  const updateIterationStatus = useUpdateIterationStatus();
  const fetchSpecificCampaign = useFetchSpecificCampaign({ teamId, campaignId });

  const [selectedReviewers, setSelectedReviewers] = useState<CognitoUser[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { navigateToCampaign } = useCampaignNavigate();
  const { unlockIteration } = useIterationLock({ teamId, campaignId, iterationId });
  const { campaign, getDynamoIterationDetails } = useCampaign();

  const iterationApi = iterationId
    ? getDynamoIterationDetails(iterationId)
    : undefined;
  if (!iterationApi) {
    return <ProgressIndicator text="Loading iteration details..." />;
  }

  const handleSubmit = async () => {
    if (selectedReviewers.length === 0) {
      setFormError('You must select at least one reviewer or go back.');
      return;
    }

    if (!teamId || !campaignId || !iterationId) {
      setFormError('Missing required information to send for review.');
      return;
    }

    try {
      setFormError(null);
      setIsSubmitting(true);
      await updateIterationStatus.mutateAsync({
        teamId,
        campaignId,
        iterationId,
        status: ITERATION_STATUS.IN_REVIEW,
        reviewers: selectedReviewers.map(({ sub }) => sub),
      });
      unlockIteration();
      await fetchSpecificCampaign.refetch();
      navigateToCampaign('summary');
    } catch {
      setFormError('Failed to send for review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentIteration = campaign?.CampaignConfig?.Iterations?.find(
    (iteration) => iteration.ID === iterationId,
  );

  return (
    <div className="iteration-reviewers-page">
      <div className="nhsuk-u-margin-bottom-6">
        <Back label="Iteration summary" backTo={`/${teamId}/campaign/${campaignId}/iteration/${iterationId}/summary`} />
      </div>
      {currentIteration && (
        <IterationPageHeader
          iteration={currentIteration}
          title="Choose an iteration reviewer"
        />
      )}
      <Card>
        <>
          <ReviewerPicker
            teamId={teamId!}
            iterationCreator={iterationApi.created_by}
            selectedReviewers={selectedReviewers}
            setSelectedReviewers={setSelectedReviewers}
            content={(
              <>
                <h3 className="nhsuk-heading-m nhsuk-u-margin-bottom-3">
                  Who should review this iteration?
                </h3>
                <p>
                  Before an iteration can be sent to NHS Notify, it needs to be
                  reviewed and approved.
                </p>
                <p>Reviewers can:</p>
                <ul>
                  <li>view the iteration</li>
                  <li>approve or reject it</li>
                </ul>
              </>
            )}
          />
          {formError && <ErrorMessage>{formError}</ErrorMessage>}
        </>
      </Card>
      <Button onClick={handleSubmit} disabled={isSubmitting}>
        {isSubmitting ? 'Updating...' : 'Send for review'}
      </Button>
    </div>
  );
}

export default IterationReviewersPage;
